import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import SelectV2 from "../basics/input/SelectV2"
import InputV2 from "../basics/input/InputV2"
import {
	DEPANNAGE_TYPE_APPEL_CHOICES,
	DEPANNAGE_TYPE_APPEL_ASSISTANCE,
	DEPANNAGE_TYPE_APPEL_POLICE,
	CLE_AVEC,
	CLE_SANS,
	ACCIDENT,
	ENLEVEMENT,
	DEP_TYPE_LIST,
	DIAGNOSTIQUE_POLICE_LABELS,
} from "../../utilities/Addons"
import {
	VEHICULE_CATEGORIE,
	VEHICULE_CATEGORIE_ORDRE,
} from "../../utilities/Utils"
import { filterEmployesGroupe } from "../../utilities/employes"
import CollapsibleCardV2 from "../basics/interface/CollapsibleCardV2"
import { formatNumDossier } from "./depFicheUtils"
import { PAYE_PAR_ASSURANCE } from "../atelier/AtelierLigneV2"

const SyncBox = () => (
	<div style={{ ...gs.inputReadonly, textAlign: "center" }}>
		<FontAwesomeIcon icon={faSync} spin />
	</div>
)

// Chip cliquable (choix exclusif ou drapeau). `active` teinte en accent.
const chip = (active) => ({
	padding: "6px 14px",
	borderRadius: 99,
	border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
	backgroundColor: active ? COLORS.accentLight : COLORS.surface,
	color: active ? COLORS.accent : COLORS.textMuted,
	fontSize: 12,
	fontWeight: 600,
	cursor: "pointer",
	fontFamily: "inherit",
})

const radioRow = { display: "flex", gap: 16, flex: 1, alignItems: "center" }
const radioLabel = {
	display: "inline-flex",
	alignItems: "center",
	gap: 6,
	fontSize: 13,
	color: COLORS.textMuted,
	cursor: "pointer",
}
const chipRow = { display: "flex", gap: 8, flexWrap: "wrap", flex: 1 }

// Titre de sous-section (dédié au bloc Assistance / Police) : liseré haut + label.
const sectionTitle = {
	fontSize: 11,
	fontWeight: 600,
	color: COLORS.textLight,
	letterSpacing: "0.08em",
	textTransform: "uppercase",
	marginTop: 8,
	marginBottom: 4,
	paddingTop: 12,
	borderTop: `1px solid ${COLORS.border}`,
}

/**
 * Carte « Intervention » : le type d'appel (Assistance / Police / Privé) en
 * chips exclusifs pilote des champs conditionnels alignés sur DepMission —
 * assistance : donneur d'ordre + n° dossier + assurance + type transport/
 * intervention ; police : clé, accident/enlèvement, secteur, catégorie. Plus le
 * dépanneur/dépanneuse et les drapeaux de course (fourrière/svp méca/sur place).
 */
const DepFicheInterventionV2 = ({
	dep,
	depInfo,
	employes,
	fetchEmployesState,
	assistances,
	secteursPolice,
	updateDep,
	updateDepField,
	setAssistance,
	refs,
	open,
	onToggle,
}) => {
	const appelType = dep.appel_type ?? -1
	const isAssurance = appelType === DEPANNAGE_TYPE_APPEL_ASSISTANCE
	const isPolice = appelType === DEPANNAGE_TYPE_APPEL_POLICE

	// Assurances (labels) de l'assistance sélectionnée.
	const selectedAssistance = (assistances || []).find(
		(a) => a.id === dep.assistance_entite,
	)
	const assurances =
		(selectedAssistance && selectedAssistance.assurances) || []
	// GE-58 : encaissements terrain payés par une assistance (police).
	const payeursAssistance = (dep.transactionsv2 || []).filter(
		(tx) =>
			tx.dep_mission &&
			tx.payeParSelect === PAYE_PAR_ASSURANCE &&
			(tx.payeur_label || tx.payeur_num_dossier),
	)

	return (
		<CollapsibleCardV2 title="Intervention" open={open} onToggle={onToggle}>
			<div style={gs.fieldGroup}>
				{/* Type d'appel */}
				<div style={gs.fieldRow}>
					<label style={gs.label}>Appel</label>
					<div style={chipRow}>
						{DEPANNAGE_TYPE_APPEL_CHOICES.map(([val, lbl]) => (
							<button
								key={`appel_type_${val}`}
								type="button"
								style={chip(appelType === val)}
								onClick={() =>
									updateDepField("appel_type", val)
								}
							>
								{lbl}
							</button>
						))}
					</div>
				</div>

				<div style={gs.fieldRow}>
					<label style={gs.label}>Dépanneur</label>
					{fetchEmployesState.loading ? (
						<SyncBox />
					) : (
						<SelectV2
							style={gs.select}
							value={dep.depanneur}
							onChange={(e) =>
								updateDep("depanneur", e, "select")
							}
							ref={refs.refDepanneur}
							nextRef={refs.refDepanneuse}
						>
							<option value={0}>-</option>
							{filterEmployesGroupe(employes, dep.depanneur).map(
								(emp) => (
									<option
										key={emp.id + emp.nom}
										value={emp.id}
									>
										{emp.nom}
									</option>
								),
							)}
						</SelectV2>
					)}
				</div>

				<div style={gs.fieldRow}>
					<label style={gs.label}>Dépanneuse</label>
					{depInfo.fetchDepVehicules.loading ? (
						<SyncBox />
					) : (
						<SelectV2
							style={gs.select}
							value={dep.vehicule_depannage}
							onChange={(e) =>
								updateDep("vehicule_depannage", e, "select")
							}
							ref={refs.refDepanneuse}
							nextRef={refs.refMarque}
						>
							<option value={0}>-</option>
							{depInfo.depVehicules
								.filter((dv) => dv.type === 0)
								.map((v) => (
									<option key={v.id + v.nom} value={v.id}>
										{v.nom}
									</option>
								))}
						</SelectV2>
					)}
				</div>

				{/* ── Bloc Assistance ── */}
				{isAssurance && (
					<>
						<div style={sectionTitle}>Assistance</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Type</label>
							<div style={chipRow}>
								{DEP_TYPE_LIST.map((t) => (
									<button
										key={`dep_type_${t.id}`}
										type="button"
										style={chip(dep.type === t.id)}
										onClick={() =>
											updateDepField("type", t.id)
										}
									>
										{t.label}
									</button>
								))}
							</div>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Assistance</label>
							<SelectV2
								style={gs.select}
								value={dep.assistance_entite}
								onChange={(e) =>
									setAssistance(parseInt(e.target.value))
								}
							>
								<option value={0}>-</option>
								{(assistances || []).map((a) => (
									<option key={`assist_${a.id}`} value={a.id}>
										{a.label}
									</option>
								))}
							</SelectV2>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>N° dossier</label>
							<InputV2
								style={gs.input}
								value={dep.num_dossier}
								ref={refs.refNDos}
								onFocus={(e) => e.target.select()}
								onChange={(e) => updateDep("num_dossier", e)}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Assurance</label>
							<SelectV2
								style={gs.select}
								value={dep.assurance}
								onChange={(e) =>
									updateDepField("assurance", e.target.value)
								}
								disabled={dep.assistance_entite === 0}
							>
								<option value="">-</option>
								{assurances.map((a) => (
									<option
										key={`assu_${a.id}`}
										value={a.label}
									>
										{a.label}
									</option>
								))}
							</SelectV2>
						</div>
					</>
				)}

				{/* ── Bloc Police ── */}
				{isPolice && (
					<>
						<div style={sectionTitle}>Police</div>
						{/* GE-52 : « Pas d'agent sur place » posé sur le terrain,
						    lecture seule (pas de bascule bureau). */}
						{dep.agent_sur_place === false ? (
							<div style={gs.fieldRow}>
								<label style={gs.label}>Matricule</label>
								<span style={gs.inputReadonly}>
									Pas d'agent sur place
								</span>
							</div>
						) : (
							<div style={gs.fieldRow}>
								<label style={gs.label}>
									Matricule
									<span style={{ color: COLORS.danger }}>
										{" "}
										*
									</span>
								</label>
								<InputV2
									style={gs.input}
									value={dep.matricule}
									ref={refs.refMat}
									onFocus={(e) => e.target.select()}
									onChange={(e) => updateDep("matricule", e)}
								/>
							</div>
						)}
						{/* GE-58 (points 3 + 12) : police payée par une assistance —
						    le payeur se lit sur la TRANSACTION terrain (partenaire
						    ou nom libre + n° de dossier), lecture seule. La fiche
						    garde la police en donneur d'ordre. */}
						{payeursAssistance.map((tx) => (
							<React.Fragment key={tx.id}>
								<div style={gs.fieldRow}>
									<label style={gs.label}>Payé par</label>
									<span style={gs.inputReadonly}>
										{tx.payeur_label || "Assistance"}
									</span>
								</div>
								{!!(tx.payeur_num_dossier || "").trim() && (
									<div style={gs.fieldRow}>
										<label style={gs.label}>
											N° dossier
										</label>
										<span style={gs.inputReadonly}>
											{formatNumDossier(
												tx.payeur_num_dossier,
											)}
										</span>
									</div>
								)}
							</React.Fragment>
						))}
						<div style={gs.fieldRow}>
							<label style={gs.label}>Clé</label>
							<div style={radioRow}>
								<label style={radioLabel}>
									<input
										type="radio"
										checked={dep.avec_cle === CLE_AVEC}
										onChange={() =>
											updateDepField("avec_cle", CLE_AVEC)
										}
									/>
									Avec
								</label>
								<label style={radioLabel}>
									<input
										type="radio"
										checked={dep.avec_cle === CLE_SANS}
										onChange={() =>
											updateDepField("avec_cle", CLE_SANS)
										}
									/>
									Sans
								</label>
							</div>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Motif</label>
							<div style={radioRow}>
								<label style={radioLabel}>
									<input
										type="radio"
										checked={
											dep.accident_ou_enlevement ===
											ACCIDENT
										}
										onChange={() =>
											updateDepField(
												"accident_ou_enlevement",
												ACCIDENT,
											)
										}
									/>
									Accident
								</label>
								<label style={radioLabel}>
									<input
										type="radio"
										checked={
											dep.accident_ou_enlevement ===
											ENLEVEMENT
										}
										onChange={() =>
											updateDepField(
												"accident_ou_enlevement",
												ENLEVEMENT,
											)
										}
									/>
									Enlèvement
								</label>
							</div>
						</div>
						{DIAGNOSTIQUE_POLICE_LABELS[
							dep.diagnostique_info?.diagnostique_police
						] && (
							<div style={gs.fieldRow}>
								<label style={gs.label}>
									Etat d'intervention police
								</label>
								<span style={gs.inputReadonly}>
									{
										DIAGNOSTIQUE_POLICE_LABELS[
											dep.diagnostique_info
												?.diagnostique_police
										]
									}
								</span>
							</div>
						)}
						<div style={gs.fieldRow}>
							<label style={gs.label}>
								Lieu d'intervention police
							</label>
							<SelectV2
								style={gs.select}
								value={dep.secteur_police}
								onChange={(e) =>
									updateDepField(
										"secteur_police",
										parseInt(e.target.value),
									)
								}
							>
								<option value={0}>-</option>
								{(secteursPolice || []).map((sec) => (
									<option key={`sp_${sec.id}`} value={sec.id}>
										{sec.label}
									</option>
								))}
							</SelectV2>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Catégorie véhicule</label>
							<SelectV2
								style={gs.select}
								value={dep.vehicule_categorie}
								onChange={(e) =>
									updateDepField(
										"vehicule_categorie",
										parseInt(e.target.value),
									)
								}
							>
								<option value={0}>-</option>
								{VEHICULE_CATEGORIE_ORDRE.map((key) => (
									<option key={`vc_${key}`} value={key}>
										{VEHICULE_CATEGORIE[key]}
									</option>
								))}
							</SelectV2>
						</div>
					</>
				)}
			</div>
		</CollapsibleCardV2>
	)
}

export default DepFicheInterventionV2
